import { planFeatureForPath } from '../constants/planFeatures';

export interface IndividualPassPurchaseRecord {
  id: string;
  purchasedAt: Date;
  periodStart: Date;
  periodEnd: Date;
  amountWon: number;
  refundedAt?: Date;
  refundAmountWon?: number;
}

/** 개별 이용권 하위: 유료·무료 이벤트 */
export type EventPassKind = 'paid' | 'free';

export interface EventPassPurchaseRecord {
  id: string;
  kind: EventPassKind;
  /** 카드·이력 표시명 */
  title: string;
  purchasedAt: Date;
  periodStart: Date;
  periodEnd: Date;
  amountWon: number;
  /** 무료 이벤트: 항상 true — 환불 UI 비활성 */
  refundDisabled: boolean;
  refundedAt?: Date;
  refundAmountWon?: number;
  /** 발급 코드(EVT-/CBT-). 표시 라벨 구분용 */
  couponCode?: string;
}

/** 이용권 스트립·이력에 쓰는 종류 라벨 */
export function eventPassKindLabel(e: Pick<EventPassPurchaseRecord, 'kind' | 'couponCode'>): string {
  const code = (e.couponCode ?? '').trim().toUpperCase();
  if (code.startsWith('CBT-')) return '베타 테스터 이용권';
  if (e.kind === 'free') return '무료 이벤트 이용권';
  return '유료 이벤트 이용권';
}

export interface GroupPassPurchaseRecord {
  id: string;
  purchasedAt: Date;
  groupName: string;
  couponEmail: string;
  purchaserAccountEmail?: string;
  quantity: number;
  activatedCount: number;
  unitProratedWon: number;
  totalWon: number;
  couponCode: string;
  activationPeriodStart: Date;
  activationPeriodEnd: Date;
  refundPeriodStart: Date;
  refundPeriodEnd: Date;
  refundedAt?: Date;
  refundAmountWon?: number;
  issuedByAdmin?: boolean;
}

export type UserPlanTier = 'FREE' | 'PLUS';

export type SubscriptionPassPersistedState = {
  individualHistory: IndividualPassPurchaseRecord[];
  groupHistory: GroupPassPurchaseRecord[];
  /** 유료·무료 이벤트 이용권 구매·적용 이력 */
  eventHistory: EventPassPurchaseRecord[];
  /**
   * 레거시·호환용 활성 이벤트 창.
   * 저장 시 활성(미환불) 이벤트 이력의 최신 구간으로 동기화합니다.
   */
  eventPassPeriod: { periodStart: Date; periodEnd: Date } | null;
  sessionGroupPassBinding: { purchaseRecordId: string; appliedAt: Date } | null;
};

/** 미환불·기간 활성인 이벤트 중 가장 최근 구매 건의 기간 */
export function deriveActiveEventPassPeriod(
  eventHistory: EventPassPurchaseRecord[]
): { periodStart: Date; periodEnd: Date } | null {
  const active = eventHistory
    .filter((r) => !r.refundedAt && isInclusiveDateRangeActive(r.periodStart, r.periodEnd))
    .sort((a, b) => b.purchasedAt.getTime() - a.purchasedAt.getTime());
  const top = active[0];
  if (!top) return null;
  return { periodStart: top.periodStart, periodEnd: top.periodEnd };
}

function emptyPassState(): SubscriptionPassPersistedState {
  return {
    individualHistory: [],
    groupHistory: [],
    eventHistory: [],
    eventPassPeriod: null,
    sessionGroupPassBinding: null,
  };
}

const LEGACY_STORAGE_KEY = 'cp.subscriptionPass.demo.v1';
const STORAGE_KEY_PREFIX = 'cp.subscriptionPass.v1';
const PASS_EPOCH_KEY_PREFIX = 'cp.subscriptionPass.epoch';
export const SUBSCRIPTION_PASS_CHANGED_EVENT = 'cp:subscription-pass-changed';

/** 학교 캐시 purge·quota 정리에서 제외할 이용권 LS 키 */
export function isSubscriptionPassStorageKey(key: string): boolean {
  return key.startsWith('cp.subscriptionPass.') || key === LEGACY_STORAGE_KEY;
}

/** 로그인 세션 기준 이용권 localStorage 스코프. resolveUserPlanTier / 기능 게이트가 동일 키를 쓰도록 바인딩합니다. */
let activePassUserId: string | null = null;

export function bindSubscriptionPassUserId(userId: string | null | undefined): void {
  const next = typeof userId === 'string' && userId.trim() ? userId.trim() : null;
  if (activePassUserId === next) return;
  activePassUserId = next;
  notifySubscriptionPassChanged();
}

export function getBoundSubscriptionPassUserId(): string | null {
  return activePassUserId;
}

function resolvePassStorageUserId(userId?: string | null): string | null {
  if (userId !== undefined) {
    return typeof userId === 'string' && userId.trim() ? userId.trim() : null;
  }
  return activePassUserId;
}

function storageKeyForUser(userId: string | null | undefined): string {
  if (userId && userId.trim()) return `${STORAGE_KEY_PREFIX}.${userId.trim()}`;
  return LEGACY_STORAGE_KEY;
}

function epochKeyForUser(userId: string): string {
  return `${PASS_EPOCH_KEY_PREFIX}.${userId}`;
}

/** 서버 pass_epoch가 로컬보다 크면 데모 이용권 이력을 비움 */
export function syncSubscriptionPassEpochFromServer(
  userId: string | null | undefined,
  serverEpoch: unknown,
): boolean {
  if (!userId || typeof window === 'undefined') return false;
  const next = typeof serverEpoch === 'number' && Number.isFinite(serverEpoch) ? serverEpoch : 0;
  let local = 0;
  try {
    const raw = window.localStorage.getItem(epochKeyForUser(userId));
    local = raw != null ? Number(raw) : 0;
    if (!Number.isFinite(local)) local = 0;
  } catch {
    local = 0;
  }
  if (next <= local) return false;
  try {
    window.localStorage.setItem(epochKeyForUser(userId), String(next));
    window.localStorage.removeItem(storageKeyForUser(userId));
    window.localStorage.removeItem(cacheMetaKeyForUser(userId));
    notifySubscriptionPassChanged();
  } catch {
    /* ignore */
  }
  return true;
}

export function clearSubscriptionPassStateForUser(userId: string | null | undefined): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(storageKeyForUser(userId));
    window.localStorage.removeItem(cacheMetaKeyForUser(userId));
    notifySubscriptionPassChanged();
  } catch {
    /* ignore */
  }
}

function startOfDayOnly(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

function endOfDayOnly(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

function isInclusiveDateRangeActive(periodStart: Date, periodEnd: Date): boolean {
  const now = Date.now();
  const s = startOfDayOnly(periodStart).getTime();
  const e = endOfDayOnly(periodEnd).getTime();
  return now >= s && now <= e;
}

function isGroupPassRegisteredOnThisSession(
  g: GroupPassPurchaseRecord,
  binding: { purchaseRecordId: string } | null
): boolean {
  return binding?.purchaseRecordId === g.id;
}

function parseDate(value: unknown): Date | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function serializeIndividual(row: IndividualPassPurchaseRecord) {
  return {
    id: row.id,
    purchasedAt: row.purchasedAt.toISOString(),
    periodStart: row.periodStart.toISOString(),
    periodEnd: row.periodEnd.toISOString(),
    amountWon: row.amountWon,
    refundedAt: row.refundedAt?.toISOString() ?? null,
    refundAmountWon: row.refundAmountWon ?? null,
  };
}

function serializeEvent(row: EventPassPurchaseRecord) {
  return {
    id: row.id,
    kind: row.kind,
    title: row.title,
    purchasedAt: row.purchasedAt.toISOString(),
    periodStart: row.periodStart.toISOString(),
    periodEnd: row.periodEnd.toISOString(),
    amountWon: row.amountWon,
    refundDisabled: row.refundDisabled === true || row.kind === 'free',
    refundedAt: row.refundedAt?.toISOString() ?? null,
    refundAmountWon: row.refundAmountWon ?? null,
    couponCode: row.couponCode ?? null,
  };
}

function deserializeEvent(raw: unknown): EventPassPurchaseRecord | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.id !== 'string') return null;
  const kind: EventPassKind = o.kind === 'free' ? 'free' : 'paid';
  const purchasedAt = parseDate(o.purchasedAt);
  const periodStart = parseDate(o.periodStart);
  const periodEnd = parseDate(o.periodEnd);
  if (!purchasedAt || !periodStart || !periodEnd) return null;
  const amountWon = Number(o.amountWon);
  if (!Number.isFinite(amountWon)) return null;
  const title =
    typeof o.title === 'string' && o.title.trim()
      ? o.title.trim()
      : kind === 'free'
        ? '무료 이벤트 이용권'
        : '유료 이벤트 이용권';
  const refundDisabled = kind === 'free' || o.refundDisabled === true;
  const refundedAt = parseDate(o.refundedAt);
  const refundAmountWon =
    o.refundAmountWon == null ? undefined : Number(o.refundAmountWon);
  const couponCode =
    typeof o.couponCode === 'string' && o.couponCode.trim() ? o.couponCode.trim() : undefined;
  return {
    id: o.id,
    kind,
    title,
    purchasedAt,
    periodStart,
    periodEnd,
    amountWon,
    refundDisabled,
    refundedAt: refundedAt ?? undefined,
    refundAmountWon: Number.isFinite(refundAmountWon) ? refundAmountWon : undefined,
    couponCode,
  };
}

function deserializeIndividual(raw: unknown): IndividualPassPurchaseRecord | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.id !== 'string') return null;
  const purchasedAt = parseDate(o.purchasedAt);
  const periodStart = parseDate(o.periodStart);
  const periodEnd = parseDate(o.periodEnd);
  if (!purchasedAt || !periodStart || !periodEnd) return null;
  const amountWon = Number(o.amountWon);
  if (!Number.isFinite(amountWon)) return null;
  const refundedAt = parseDate(o.refundedAt);
  const refundAmountWon =
    o.refundAmountWon == null ? undefined : Number(o.refundAmountWon);
  return {
    id: o.id,
    purchasedAt,
    periodStart,
    periodEnd,
    amountWon,
    refundedAt: refundedAt ?? undefined,
    refundAmountWon: Number.isFinite(refundAmountWon) ? refundAmountWon : undefined,
  };
}

function serializeGroup(row: GroupPassPurchaseRecord) {
  return {
    id: row.id,
    purchasedAt: row.purchasedAt.toISOString(),
    groupName: row.groupName,
    couponEmail: row.couponEmail,
    purchaserAccountEmail: row.purchaserAccountEmail ?? null,
    quantity: row.quantity,
    activatedCount: row.activatedCount,
    unitProratedWon: row.unitProratedWon,
    totalWon: row.totalWon,
    couponCode: row.couponCode,
    activationPeriodStart: row.activationPeriodStart.toISOString(),
    activationPeriodEnd: row.activationPeriodEnd.toISOString(),
    refundPeriodStart: row.refundPeriodStart.toISOString(),
    refundPeriodEnd: row.refundPeriodEnd.toISOString(),
    refundedAt: row.refundedAt?.toISOString() ?? null,
    refundAmountWon: row.refundAmountWon ?? null,
    issuedByAdmin: row.issuedByAdmin === true,
  };
}

function deserializeGroup(raw: unknown): GroupPassPurchaseRecord | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.id !== 'string' || typeof o.groupName !== 'string') return null;
  const purchasedAt = parseDate(o.purchasedAt);
  const activationPeriodStart = parseDate(o.activationPeriodStart);
  const activationPeriodEnd = parseDate(o.activationPeriodEnd);
  const refundPeriodStart = parseDate(o.refundPeriodStart);
  const refundPeriodEnd = parseDate(o.refundPeriodEnd);
  if (
    !purchasedAt ||
    !activationPeriodStart ||
    !activationPeriodEnd ||
    !refundPeriodStart ||
    !refundPeriodEnd
  ) {
    return null;
  }
  const quantity = Number(o.quantity);
  const activatedCount = Number(o.activatedCount);
  const unitProratedWon = Number(o.unitProratedWon);
  const totalWon = Number(o.totalWon);
  if (
    !Number.isFinite(quantity) ||
    !Number.isFinite(activatedCount) ||
    !Number.isFinite(unitProratedWon) ||
    !Number.isFinite(totalWon)
  ) {
    return null;
  }
  const refundedAt = parseDate(o.refundedAt);
  const refundAmountWon =
    o.refundAmountWon == null ? undefined : Number(o.refundAmountWon);
  return {
    id: o.id,
    purchasedAt,
    groupName: o.groupName,
    couponEmail: typeof o.couponEmail === 'string' ? o.couponEmail : '',
    purchaserAccountEmail:
      typeof o.purchaserAccountEmail === 'string' ? o.purchaserAccountEmail : undefined,
    quantity,
    activatedCount,
    unitProratedWon,
    totalWon,
    couponCode: typeof o.couponCode === 'string' ? o.couponCode : '',
    activationPeriodStart,
    activationPeriodEnd,
    refundPeriodStart,
    refundPeriodEnd,
    refundedAt: refundedAt ?? undefined,
    refundAmountWon: Number.isFinite(refundAmountWon) ? refundAmountWon : undefined,
    issuedByAdmin: o.issuedByAdmin === true,
  };
}

export function buildSubscriptionPassStateRecord(
  state: SubscriptionPassPersistedState
): Record<string, unknown> {
  const eventPassPeriod =
    deriveActiveEventPassPeriod(state.eventHistory) ?? state.eventPassPeriod;
  return {
    individualHistory: state.individualHistory.map(serializeIndividual),
    groupHistory: state.groupHistory.map(serializeGroup),
    eventHistory: state.eventHistory.map(serializeEvent),
    eventPassPeriod: eventPassPeriod
      ? {
          periodStart: eventPassPeriod.periodStart.toISOString(),
          periodEnd: eventPassPeriod.periodEnd.toISOString(),
        }
      : null,
    sessionGroupPassBinding: state.sessionGroupPassBinding
      ? {
          purchaseRecordId: state.sessionGroupPassBinding.purchaseRecordId,
          appliedAt: state.sessionGroupPassBinding.appliedAt.toISOString(),
        }
      : null,
  };
}

export function parseSubscriptionPassStateRecord(
  parsed: Record<string, unknown>
): SubscriptionPassPersistedState {
  const individualHistory = Array.isArray(parsed.individualHistory)
    ? parsed.individualHistory
        .map(deserializeIndividual)
        .filter((row): row is IndividualPassPurchaseRecord => row != null)
    : [];
  const groupHistory = Array.isArray(parsed.groupHistory)
    ? parsed.groupHistory
        .map(deserializeGroup)
        .filter((row): row is GroupPassPurchaseRecord => row != null)
    : [];
  let eventHistory = Array.isArray(parsed.eventHistory)
    ? parsed.eventHistory
        .map(deserializeEvent)
        .filter((row): row is EventPassPurchaseRecord => row != null)
    : [];
  let eventPassPeriod: SubscriptionPassPersistedState['eventPassPeriod'] = null;
  if (parsed.eventPassPeriod && typeof parsed.eventPassPeriod === 'object') {
    const ep = parsed.eventPassPeriod as Record<string, unknown>;
    const periodStart = parseDate(ep.periodStart);
    const periodEnd = parseDate(ep.periodEnd);
    if (periodStart && periodEnd) {
      eventPassPeriod = { periodStart, periodEnd };
    }
  }
  if (eventHistory.length === 0 && eventPassPeriod) {
    eventHistory = [
      {
        id: `evt-legacy-${eventPassPeriod.periodStart.getTime()}`,
        kind: 'paid',
        title: '유료 이벤트 이용권',
        purchasedAt: eventPassPeriod.periodStart,
        periodStart: eventPassPeriod.periodStart,
        periodEnd: eventPassPeriod.periodEnd,
        amountWon: 0,
        refundDisabled: false,
      },
    ];
  }
  eventPassPeriod = deriveActiveEventPassPeriod(eventHistory) ?? eventPassPeriod;
  let sessionGroupPassBinding: SubscriptionPassPersistedState['sessionGroupPassBinding'] = null;
  if (parsed.sessionGroupPassBinding && typeof parsed.sessionGroupPassBinding === 'object') {
    const binding = parsed.sessionGroupPassBinding as Record<string, unknown>;
    const purchaseRecordId =
      typeof binding.purchaseRecordId === 'string' ? binding.purchaseRecordId : '';
    const appliedAt = parseDate(binding.appliedAt);
    if (purchaseRecordId && appliedAt) {
      sessionGroupPassBinding = { purchaseRecordId, appliedAt };
    }
  }
  return {
    individualHistory,
    groupHistory,
    eventHistory,
    eventPassPeriod,
    sessionGroupPassBinding,
  };
}

const CACHE_META_KEY_PREFIX = 'cp.subscriptionPass.meta.v1';

export type SubscriptionPassCacheMeta = {
  serverUpdatedAt: string | null;
  serverRevision: number;
};

function cacheMetaKeyForUser(userId: string | null | undefined): string {
  const id = resolvePassStorageUserId(userId);
  return id ? `${CACHE_META_KEY_PREFIX}.${id}` : `${CACHE_META_KEY_PREFIX}.legacy`;
}

export function loadSubscriptionPassCacheMeta(
  userId?: string | null
): SubscriptionPassCacheMeta {
  if (typeof window === 'undefined') return { serverUpdatedAt: null, serverRevision: 0 };
  try {
    const raw = window.localStorage.getItem(cacheMetaKeyForUser(userId));
    if (!raw) return { serverUpdatedAt: null, serverRevision: 0 };
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const serverUpdatedAt =
      typeof parsed.serverUpdatedAt === 'string' ? parsed.serverUpdatedAt : null;
    const serverRevision =
      typeof parsed.serverRevision === 'number' && Number.isFinite(parsed.serverRevision)
        ? Math.floor(parsed.serverRevision)
        : 0;
    return { serverUpdatedAt, serverRevision };
  } catch {
    return { serverUpdatedAt: null, serverRevision: 0 };
  }
}

/** 서버 head(revision)와 함께 저장된 캐시만 권한 판정에 사용한다. */
export function isSubscriptionPassCacheVerified(userId?: string | null): boolean {
  const meta = loadSubscriptionPassCacheMeta(userId);
  return !!meta.serverUpdatedAt && meta.serverRevision > 0;
}

/** 검증된 캐시가 없으면 null(미확정). */
export function resolveVerifiedUserPlanTier(userId?: string | null): UserPlanTier | null {
  if (!isSubscriptionPassCacheVerified(userId)) return null;
  return resolveUserPlanTier(loadSubscriptionPassState(userId));
}

export function saveSubscriptionPassCacheMeta(
  meta: SubscriptionPassCacheMeta,
  userId?: string | null
): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(cacheMetaKeyForUser(userId), JSON.stringify(meta));
  } catch {
    /* ignore */
  }
}

export function loadSubscriptionPassState(
  userId?: string | null,
): SubscriptionPassPersistedState {
  if (typeof window === 'undefined') {
    return emptyPassState();
  }
  try {
    const scopedUserId = resolvePassStorageUserId(userId);
    const key = storageKeyForUser(scopedUserId);
    let raw = window.localStorage.getItem(key);
    // 로그인 직후 유저 키에 없으면 레거시 데모 키에서 한 번 마이그레이션
    if (!raw && scopedUserId) {
      const legacy = window.localStorage.getItem(LEGACY_STORAGE_KEY);
      if (legacy) {
        window.localStorage.setItem(key, legacy);
        raw = legacy;
      }
    }
    if (!raw) {
      return emptyPassState();
    }
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return parseSubscriptionPassStateRecord(parsed);
  } catch {
    return emptyPassState();
  }
}

export function saveSubscriptionPassState(
  state: SubscriptionPassPersistedState,
  userId?: string | null,
  serverMeta?: SubscriptionPassCacheMeta | null,
): void {
  if (typeof window === 'undefined') return;
  try {
    const scopedUserId = resolvePassStorageUserId(userId);
    window.localStorage.setItem(
      storageKeyForUser(scopedUserId),
      JSON.stringify(buildSubscriptionPassStateRecord(state))
    );
    if (serverMeta) {
      saveSubscriptionPassCacheMeta(serverMeta, scopedUserId);
    }
    notifySubscriptionPassChanged();
  } catch {
    // ignore quota errors
  }
}

export function notifySubscriptionPassChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(SUBSCRIPTION_PASS_CHANGED_EVENT));
}

export function hasActivePaidSubscription(
  state: SubscriptionPassPersistedState = loadSubscriptionPassState()
): boolean {
  const hasActiveIndividual = state.individualHistory.some(
    (row) => !row.refundedAt && isInclusiveDateRangeActive(row.periodStart, row.periodEnd)
  );
  if (hasActiveIndividual) return true;

  const hasActiveGroup = state.groupHistory.some(
    (row) =>
      !row.refundedAt &&
      isInclusiveDateRangeActive(row.activationPeriodStart, row.activationPeriodEnd) &&
      // 세션 바인딩이 없으면 활성 그룹 이력만으로 유료 인정(레거시·동기화 누락 대비).
      // 바인딩이 있으면 해당 건만 인정.
      (state.sessionGroupPassBinding == null ||
        isGroupPassRegisteredOnThisSession(row, state.sessionGroupPassBinding))
  );
  if (hasActiveGroup) return true;

  if (
    state.eventHistory.some(
      (row) => !row.refundedAt && isInclusiveDateRangeActive(row.periodStart, row.periodEnd)
    )
  ) {
    return true;
  }

  if (state.eventPassPeriod) {
    return isInclusiveDateRangeActive(
      state.eventPassPeriod.periodStart,
      state.eventPassPeriod.periodEnd
    );
  }

  return false;
}

export function resolveUserPlanTier(
  state: SubscriptionPassPersistedState = loadSubscriptionPassState()
): UserPlanTier {
  return hasActivePaidSubscription(state) ? 'PLUS' : 'FREE';
}

/** 관리자 통계용: 활성 이용권 기준 계정 유형(배타 분류) */
export type AccountPassCategory =
  | 'beta'
  | 'free'
  | 'eventFree'
  | 'eventPaid'
  | 'individual'
  | 'group';

export const ACCOUNT_PASS_CATEGORY_LABEL: Record<AccountPassCategory, string> = {
  beta: 'PLUS - 베타 테스터 무료',
  free: 'FREE - 프리',
  eventFree: 'PLUS - 이벤트 무료',
  eventPaid: 'PLUS - 이벤트 유료',
  individual: 'PLUS - 개별 유료',
  group: 'PLUS - 그룹 유료',
};

/** 통계 미니 블럭: 윗줄 tier / 아랫줄 상세 */
export const ACCOUNT_PASS_CATEGORY_PARTS: Record<
  AccountPassCategory,
  { tier: 'FREE' | 'PLUS'; detail: string }
> = {
  free: { tier: 'FREE', detail: '프리' },
  beta: { tier: 'PLUS', detail: '베타 테스터 무료' },
  eventFree: { tier: 'PLUS', detail: '이벤트 무료' },
  eventPaid: { tier: 'PLUS', detail: '이벤트 유료' },
  individual: { tier: 'PLUS', detail: '개별 유료' },
  group: { tier: 'PLUS', detail: '그룹 유료' },
};

function isNonBetaActiveEventPass(row: EventPassPurchaseRecord): boolean {
  if (row.refundedAt) return false;
  if (!isInclusiveDateRangeActive(row.periodStart, row.periodEnd)) return false;
  const code = (row.couponCode ?? '').trim().toUpperCase().replace(/[\s-]/g, '');
  if (code.startsWith('CBT')) return false;
  const title = (row.title ?? '').trim();
  if (/베타\s*테스터|베타 테스터 이용권/i.test(title)) return false;
  return true;
}

/** 이벤트 코드(EVT)·kind=free → 무료, 이벤트 카드 결제 → 유료 */
export function isFreeEventPassRecord(row: EventPassPurchaseRecord): boolean {
  if (row.kind === 'free') return true;
  const code = (row.couponCode ?? '').trim().toUpperCase().replace(/[\s-]/g, '');
  return code.startsWith('EVT');
}

/** 활성 CBT(베타 테스터) 이용권 여부 — 사이드 메뉴·피드백 페이지 공통 */
export function hasActiveBetaTesterPass(
  state: SubscriptionPassPersistedState = loadSubscriptionPassState(),
): boolean {
  return state.eventHistory.some((row) => {
    if (row.refundedAt) return false;
    if (!isInclusiveDateRangeActive(row.periodStart, row.periodEnd)) return false;
    const code = (row.couponCode ?? '').trim().toUpperCase().replace(/[\s-]/g, '');
    if (code.startsWith('CBT')) return true;
    const title = (row.title ?? '').trim();
    return /베타\s*테스터|베타 테스터 이용권/i.test(title);
  });
}

/**
 * 우선순위: 베타 → 개별유료 → 그룹유료 → 이벤트유료 → 이벤트무료 → 프리.
 * 그룹은 세션 바인딩 없이 활성 기간만 본다(플랫폼 집계용).
 */
export function resolveAccountPassCategory(
  state: SubscriptionPassPersistedState,
): AccountPassCategory {
  if (hasActiveBetaTesterPass(state)) return 'beta';

  const hasIndividual = state.individualHistory.some(
    (row) => !row.refundedAt && isInclusiveDateRangeActive(row.periodStart, row.periodEnd),
  );
  if (hasIndividual) return 'individual';

  const hasGroup = state.groupHistory.some(
    (row) =>
      !row.refundedAt &&
      isInclusiveDateRangeActive(row.activationPeriodStart, row.activationPeriodEnd),
  );
  if (hasGroup) return 'group';

  const activeEvents = state.eventHistory.filter(isNonBetaActiveEventPass);
  if (activeEvents.some((row) => !isFreeEventPassRecord(row))) return 'eventPaid';
  if (activeEvents.some((row) => isFreeEventPassRecord(row))) return 'eventFree';

  if (
    state.eventPassPeriod &&
    isInclusiveDateRangeActive(state.eventPassPeriod.periodStart, state.eventPassPeriod.periodEnd)
  ) {
    // 레거시 기간만 있는 경우 구분 불가 → 이벤트 코드(무료)로 취급
    return 'eventFree';
  }

  return 'free';
}

/** 개별·그룹·이벤트 등 등록된 이용권이 있으면 전체 기능 이용 가능 */
export function canUsePaidPassFeatures(
  state: SubscriptionPassPersistedState = loadSubscriptionPassState()
): boolean {
  return hasActivePaidSubscription(state);
}

/** 회원가입 기본 프리 이용권만 있는 상태(유료·등록 이용권 없음) */
export function isFreePassOnly(
  state: SubscriptionPassPersistedState = loadSubscriptionPassState()
): boolean {
  return !canUsePaidPassFeatures(state);
}

/** 프리 이용권에서 클래스 등록 상한 */
export const FREE_PASS_MAX_CLASSES = 1;

/** PLUS(유료) 이용권에서 클래스 등록 상한 */
export const PLUS_PASS_MAX_CLASSES = 6;

/** 현재 플랜의 클래스 등록 상한 */
export function getMaxClassesForPass(isFreePass: boolean): number {
  return isFreePass ? FREE_PASS_MAX_CLASSES : PLUS_PASS_MAX_CLASSES;
}

/** 프리에서 이용권 구매가 필요한 앱 경로(딥링크·사이드메뉴 공통). 경로별 기능 키는 planFeatureForPath */
export function isPaidPassRequiredPath(pathname: string): boolean {
  return planFeatureForPath(pathname) != null;
}
